import { useState, useMemo } from 'react';
import { useTrashItems, useRestoreTrashItem, usePermanentDelete, TrashItem } from '@/hooks/useTrash';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Loader2, Trash2, RotateCcw, Package, FileText, ShoppingCart, 
  AlertTriangle, Search, X, ChevronDown, ChevronRight, FolderOpen, List, Layers 
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import Header from '@/components/layout/Header';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { PermanentDeleteDialog } from '@/components/trash/PermanentDeleteDialog';
import { toast } from 'sonner';

const typeConfig = {
  item: {
    label: 'Items',
    icon: Package,
    color: 'bg-blue-500/10 text-blue-500 border-blue-500/20'
  },
  spec: {
    label: 'Specs',
    icon: FileText,
    color: 'bg-purple-500/10 text-purple-500 border-purple-500/20'
  },
  order: {
    label: 'Đơn hàng',
    icon: ShoppingCart,
    color: 'bg-green-500/10 text-green-500 border-green-500/20'
  }
};

interface TrashItemCardProps {
  item: TrashItem;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  onRestore: () => void;
  isRestoring: boolean;
  isAdmin: boolean;
  onPermanentDelete: () => void;
  isDeletingPermanently: boolean;
}

function TrashItemCard({ 
  item, 
  isSelected, 
  onSelect, 
  onRestore, 
  isRestoring, 
  isAdmin, 
  onPermanentDelete, 
  isDeletingPermanently 
}: TrashItemCardProps) {
  const config = typeConfig[item.type];
  const Icon = config.icon;
  const isUrgent = item.days_remaining <= 7;

  return (
    <div className={cn(
      "flex items-center justify-between p-4 border rounded-lg bg-card hover:bg-accent/50 transition-colors",
      isSelected && "ring-2 ring-primary bg-primary/5"
    )}>
      <div className="flex items-center gap-4">
        <Checkbox 
          checked={isSelected}
          onCheckedChange={onSelect}
          className="h-5 w-5"
          aria-label={`Chọn ${item.name}`}
        />
        
        <div className={cn(
          "p-2 rounded-lg border",
          config.color
        )}>
          <Icon className="h-5 w-5" />
        </div>
        
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{item.name}</span>
            <Badge variant="outline" className={config.color}>
              {config.label}
            </Badge>
          </div>
          
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>{item.project_name}</span>
            <span>•</span>
            <span>
              Đã xóa {formatDistanceToNow(new Date(item.deleted_at), { 
                addSuffix: true, 
                locale: vi 
              })}
            </span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <div className={cn(
          "text-sm px-2 py-1 rounded",
          isUrgent 
            ? "bg-destructive/10 text-destructive" 
            : "bg-muted text-muted-foreground"
        )}>
          {isUrgent && <AlertTriangle className="h-3 w-3 inline mr-1" />}
          {item.days_remaining} ngày còn lại
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={onRestore}
          disabled={isRestoring || isDeletingPermanently}
        >
          {isRestoring ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <RotateCcw className="h-4 w-4 mr-1" />
              Khôi phục
            </>
          )}
        </Button>

        {isAdmin && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onPermanentDelete}
            disabled={isRestoring || isDeletingPermanently}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            {isDeletingPermanently ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

type GroupMode = 'none' | 'project' | 'type';

export default function TrashPage() {
  const [activeTab, setActiveTab] = useState<'all' | 'item' | 'spec' | 'order'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [groupMode, setGroupMode] = useState<GroupMode>('none');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  const { data: trashItems = [], isLoading, error } = useTrashItems();
  const restoreItem = useRestoreTrashItem();
  const permanentDelete = usePermanentDelete();
  const { isAdmin } = useAdminCheck();
  
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteDialogItem, setDeleteDialogItem] = useState<TrashItem | null>(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [isBulkRestoring, setIsBulkRestoring] = useState(false);

  // Get unique projects from trash items
  const uniqueProjects = useMemo(() => 
    Array.from(new Set(trashItems.map(item => item.project_name)))
      .filter(Boolean)
      .sort(),
    [trashItems]
  );

  const handleRestore = async (item: TrashItem) => {
    setRestoringId(item.id);
    try {
      await restoreItem.mutateAsync({ id: item.id, type: item.type });
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentDelete = async () => {
    if (!deleteDialogItem) return;
    setDeletingId(deleteDialogItem.id);
    try {
      await permanentDelete.mutateAsync({ id: deleteDialogItem.id, type: deleteDialogItem.type });
      setDeleteDialogItem(null);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredItems = useMemo(() => {
    return trashItems.filter(item => {
      const matchesType = activeTab === 'all' || item.type === activeTab;
      const searchLower = searchQuery.toLowerCase().trim();
      const matchesSearch = !searchLower || 
        item.name.toLowerCase().includes(searchLower) ||
        item.project_name.toLowerCase().includes(searchLower);
      const matchesProject = projectFilter === 'all' || item.project_name === projectFilter;
      return matchesType && matchesSearch && matchesProject;
    });
  }, [trashItems, activeTab, searchQuery, projectFilter]);

  // Selection helpers
  const getItemKey = (item: TrashItem) => `${item.type}-${item.id}`;
  
  const toggleItem = (itemKey: string, checked: boolean) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(itemKey);
      } else {
        next.delete(itemKey);
      }
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(filteredItems.map(getItemKey)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const selectedTrashItems = filteredItems.filter(item => selectedItems.has(getItemKey(item)));
  const allSelected = filteredItems.length > 0 && selectedItems.size === filteredItems.length;

  // Bulk actions
  const handleBulkRestore = async () => {
    const items = selectedTrashItems;
    if (items.length === 0) return;
    
    setIsBulkRestoring(true);
    let successCount = 0;
    
    for (const item of items) {
      try {
        await restoreItem.mutateAsync({ id: item.id, type: item.type });
        successCount++;
      } catch (e) {
        // Continue with others
      }
    }
    
    setIsBulkRestoring(false);
    if (successCount > 0) {
      toast.success(`Đã khôi phục ${successCount} mục`);
      setSelectedItems(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const items = selectedTrashItems;
    if (items.length === 0) return;
    
    let successCount = 0;
    
    for (const item of items) {
      try {
        await permanentDelete.mutateAsync({ id: item.id, type: item.type });
        successCount++;
      } catch (e) {
        // Continue with others
      }
    }
    
    if (successCount > 0) {
      toast.success(`Đã xóa vĩnh viễn ${successCount} mục`);
      setSelectedItems(new Set());
    }
    setShowBulkDeleteDialog(false);
  };

  // Grouping logic
  const groupedItems = useMemo(() => {
    if (groupMode === 'none') return null;
    
    const groups: Record<string, TrashItem[]> = {};
    
    for (const item of filteredItems) {
      const key = groupMode === 'project' ? item.project_name : typeConfig[item.type].label;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    }
    
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredItems, groupMode]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  // Clear selection when filters change
  const hasActiveFilters = searchQuery || projectFilter !== 'all';

  const itemCounts = {
    all: trashItems.length,
    item: trashItems.filter(i => i.type === 'item').length,
    spec: trashItems.filter(i => i.type === 'spec').length,
    order: trashItems.filter(i => i.type === 'order').length,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-6">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-6">
          <div className="flex items-center justify-center h-64 text-destructive">
            Lỗi tải dữ liệu: {(error as Error).message}
          </div>
        </main>
      </div>
    );
  }

  const renderItemCard = (item: TrashItem) => (
    <TrashItemCard
      key={getItemKey(item)}
      item={item}
      isSelected={selectedItems.has(getItemKey(item))}
      onSelect={(checked) => toggleItem(getItemKey(item), checked)}
      onRestore={() => handleRestore(item)}
      isRestoring={restoringId === item.id}
      isAdmin={isAdmin}
      onPermanentDelete={() => setDeleteDialogItem(item)}
      isDeletingPermanently={deletingId === item.id}
    />
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-6">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-destructive/10 rounded-lg">
                    <Trash2 className="h-6 w-6 text-destructive" />
                  </div>
                  <div>
                    <CardTitle>Thùng rác</CardTitle>
                    <CardDescription>
                      Các mục đã xóa sẽ bị xóa vĩnh viễn sau 30 ngày
                    </CardDescription>
                  </div>
                </div>
                
                {/* Grouping Toggle */}
                <div className="flex items-center gap-1 border rounded-lg p-1">
                  <Button
                    variant={groupMode === 'none' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setGroupMode('none')}
                    className="h-8"
                  >
                    <List className="h-4 w-4 mr-1" />
                    Danh sách
                  </Button>
                  <Button
                    variant={groupMode === 'project' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setGroupMode('project')}
                    className="h-8"
                  >
                    <FolderOpen className="h-4 w-4 mr-1" />
                    Theo dự án
                  </Button>
                  <Button
                    variant={groupMode === 'type' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setGroupMode('type')}
                    className="h-8"
                  >
                    <Layers className="h-4 w-4 mr-1" />
                    Theo loại
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Tìm theo tên hoặc dự án..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                <Select value={projectFilter} onValueChange={setProjectFilter}>
                  <SelectTrigger className="w-full sm:w-[200px]">
                    <SelectValue placeholder="Lọc theo dự án" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tất cả dự án</SelectItem>
                    {uniqueProjects.map(project => (
                      <SelectItem key={project} value={project}>
                        {project}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {hasActiveFilters && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="shrink-0"
                    onClick={() => {
                      setSearchQuery('');
                      setProjectFilter('all');
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Xóa bộ lọc
                  </Button>
                )}
              </div>

              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
                <TabsList className="mb-4">
                  <TabsTrigger value="all">
                    Tất cả ({itemCounts.all})
                  </TabsTrigger>
                  <TabsTrigger value="item">
                    <Package className="h-4 w-4 mr-1" />
                    Items ({itemCounts.item})
                  </TabsTrigger>
                  <TabsTrigger value="spec">
                    <FileText className="h-4 w-4 mr-1" />
                    Specs ({itemCounts.spec})
                  </TabsTrigger>
                  <TabsTrigger value="order">
                    <ShoppingCart className="h-4 w-4 mr-1" />
                    Đơn hàng ({itemCounts.order})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value={activeTab} className="mt-0">
                  {filteredItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Trash2 className="h-12 w-12 mb-4 opacity-50" />
                      <p>{hasActiveFilters ? 'Không tìm thấy kết quả' : 'Thùng rác trống'}</p>
                      <p className="text-sm">
                        {hasActiveFilters 
                          ? 'Thử thay đổi từ khóa hoặc bộ lọc'
                          : 'Các mục đã xóa sẽ xuất hiện ở đây'}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Select All Header */}
                      <div className="flex items-center gap-3 pb-3 border-b mb-3">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={toggleAll}
                          className="h-5 w-5"
                          aria-label="Chọn tất cả"
                        />
                        <span className="text-sm text-muted-foreground">
                          {selectedItems.size > 0 
                            ? `Đã chọn ${selectedItems.size}/${filteredItems.length}`
                            : 'Chọn tất cả'
                          }
                        </span>
                      </div>

                      {/* Grouped or Flat List */}
                      {groupMode !== 'none' && groupedItems ? (
                        <div className="space-y-3">
                          {groupedItems.map(([groupKey, items]) => (
                            <Collapsible 
                              key={groupKey} 
                              open={expandedGroups.has(groupKey)}
                              onOpenChange={() => toggleGroup(groupKey)}
                            >
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors">
                                  {expandedGroups.has(groupKey) ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                  <span className="font-medium">{groupKey}</span>
                                  <Badge variant="secondary">{items.length}</Badge>
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="pt-2 space-y-2">
                                {items.map(renderItemCard)}
                              </CollapsibleContent>
                            </Collapsible>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {filteredItems.map(renderItemCard)}
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Bulk Action Bar */}
      {selectedItems.size > 0 && (
        <Card className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 shadow-lg border-2 border-primary/20 bg-background/95 backdrop-blur-sm">
          <div className="flex items-center gap-4 p-4">
            <Badge variant="secondary" className="text-sm">
              {selectedItems.size} đã chọn
            </Badge>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkRestore}
              disabled={isBulkRestoring}
            >
              {isBulkRestoring ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-1" />
              )}
              Khôi phục
            </Button>
            
            {isAdmin && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowBulkDeleteDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Xóa vĩnh viễn
              </Button>
            )}
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSelectedItems(new Set())}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}

      <PermanentDeleteDialog
        open={!!deleteDialogItem}
        onOpenChange={(open) => !open && setDeleteDialogItem(null)}
        item={deleteDialogItem}
        onConfirm={handlePermanentDelete}
        isDeleting={permanentDelete.isPending}
      />

      {/* Bulk Delete Dialog */}
      <PermanentDeleteDialog
        open={showBulkDeleteDialog}
        onOpenChange={setShowBulkDeleteDialog}
        item={selectedTrashItems.length > 0 ? {
          ...selectedTrashItems[0],
          name: `${selectedTrashItems.length} mục đã chọn`
        } : null}
        onConfirm={handleBulkDelete}
        isDeleting={permanentDelete.isPending}
      />
    </div>
  );
}
